// 005 FR-024 (P-PROD-5): image (or the category's placeholder), name, kind and class, publisher,
// price, and Add or the stepper; a Mandatory/Optional tag for booklist items; "Coming soon" for
// products that can't be ordered; greyed "Not available" for unavailable booklist items.
import { useState } from "react";
import { Image, StyleSheet, View } from "react-native";

import { standardClassName } from "@/profile/viewModels";
import { makeStyles } from "@/theme/makeStyles";
import { Text } from "@/ui/Text";

import { SHOP_COPY } from "./copy";
import { formatPaise, kindLabel } from "./format";
import { QuantityControl } from "./QuantityControl";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    card: {
      flex: 1,
      minWidth: 150,
      padding: t.space.md,
      gap: t.space.xs,
      borderRadius: t.radius.card,
      backgroundColor: t.color.surface,
    },
    greyed: { opacity: 0.55 },
    image: {
      width: "100%",
      aspectRatio: 1,
      borderRadius: t.radius.button,
      backgroundColor: t.color.surfaceAlt,
    },
    tags: { flexDirection: "row", gap: t.space.xs, flexWrap: "wrap" },
    tag: {
      paddingHorizontal: t.space.xs,
      paddingVertical: 2,
      borderRadius: t.radius.pill,
      backgroundColor: t.color.primarySoft,
    },
    tagText: { color: t.color.primaryText },
    optional: { backgroundColor: t.color.surfaceAlt },
    grow: { flexGrow: 1 },
  }),
);

export type CardProduct = {
  id: string;
  name: string;
  kind: string;
  classLevels?: readonly string[];
  publisher?: string | null;
  priceInPaise: number;
  imageUrl?: string | null;
  orderable: boolean;
  available?: boolean;
  /** Booklist items: their tag and the booklist quantity. */
  booklist?: { mandatory: boolean; quantity: number };
};

export function ProductCard({
  product,
  quantity,
  pending,
  disabled,
  onChange,
}: {
  product: CardProduct;
  quantity: number;
  pending: boolean;
  disabled: boolean;
  onChange: (next: number) => void;
}) {
  const styles = useStyles();
  const [imageFailed, setImageFailed] = useState(false);
  const unavailable = product.available === false;
  const classes =
    product.classLevels && product.classLevels.length === 1
      ? ` · ${standardClassName(product.classLevels[0]!)}`
      : "";
  return (
    <View
      style={[styles.card, unavailable && styles.greyed]}
      testID={`product-${product.id}`}
      aria-label={product.name}
    >
      {product.imageUrl && !imageFailed ? (
        <Image
          source={{ uri: product.imageUrl }}
          style={styles.image}
          accessibilityIgnoresInvertColors
          alt=""
          onError={() => setImageFailed(true)}
        />
      ) : (
        <View style={styles.image} aria-hidden />
      )}
      {product.booklist ? (
        <View style={styles.tags}>
          <View
            style={[styles.tag, !product.booklist.mandatory && styles.optional]}
          >
            <Text
              size="xxs"
              weight="semiBold"
              style={product.booklist.mandatory ? styles.tagText : undefined}
            >
              {product.booklist.mandatory
                ? SHOP_COPY.mandatory
                : SHOP_COPY.optional}
            </Text>
          </View>
          {product.booklist.quantity > 1 ? (
            <Text
              size="xxs"
              tone="muted"
            >{`× ${product.booklist.quantity}`}</Text>
          ) : null}
        </View>
      ) : null}
      <Text
        weight="semiBold"
        numberOfLines={2}
        accessibilityLabel={product.name}
      >
        {product.name}
      </Text>
      <Text size="sm" tone="muted" numberOfLines={1}>
        {`${kindLabel(product.kind)}${classes}`}
      </Text>
      {product.publisher ? (
        <Text size="sm" tone="muted" numberOfLines={1}>
          {product.publisher}
        </Text>
      ) : null}
      <View style={styles.grow} />
      <Text weight="bold">{formatPaise(product.priceInPaise)}</Text>
      {unavailable ? (
        <Text size="sm" weight="semiBold" tone="muted">
          {SHOP_COPY.notAvailable}
        </Text>
      ) : !product.orderable ? (
        <Text size="sm" weight="semiBold" tone="muted">
          {SHOP_COPY.comingSoon}
        </Text>
      ) : (
        <QuantityControl
          name={product.name}
          quantity={quantity}
          pending={pending}
          disabled={disabled}
          onChange={onChange}
          testID={`qty-${product.id}`}
        />
      )}
    </View>
  );
}
